/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from 'react';
import { Box, Flex, Link } from '@chakra-ui/react';
import { Colors, Icon, Text } from '@mercator-gcp/grout';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { SidebarLayout } from 'components/layout/SidebarLayout';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { trackPageView, trackSecondaryNavigation } from 'utils/analytics';
import {
  SecondaryNavigationClickActions,
  SecondaryNavigationClickLocations,
} from 'utils/analytics/constants';
import { FLAGS } from 'utils/featureFlags';

const navMap = {
  PROFILE: 'Profile',
  NOTIFICATION: 'Notifications',
  USERS: 'Users',
  BILLING: 'Billing',
  INTEGRATIONS: 'Integrations',
};

const tabToUrlMap = {
  [navMap.PROFILE]: 'my-profile',
  [navMap.NOTIFICATION]: 'notifications',
  [navMap.USERS]: 'users',
  [navMap.BILLING]: 'billing',
  [navMap.INTEGRATIONS]: 'integrations',
};

const navIconMap = {
  [navMap.PROFILE]: Icon.Name.User,
  [navMap.NOTIFICATION]: Icon.Name.Email,
  [navMap.USERS]: Icon.Name.AddUser,
  [navMap.BILLING]: Icon.Name.Transaction,
  [navMap.INTEGRATIONS]: Icon.Name.Link,
};

const navListStyle = {
  paddingTop: 'padding_16',
  gap: 'padding_4',
};

const menuItemStyle = {
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 'padding_8',
};

const containerStyle = {
  paddingY: 'padding_24',
  paddingX: 'padding_32',
  overflowY: 'auto',
  height: '100%',
  width: '100%',
};

const mainContentStyle = {
  width: '100%',
  maxWidth: '800px',
  marginX: 'auto',
};

const sidebarContentStyle = {
  padding: 'padding_16',
};

export const AccountSettings = () => {
  const [selectedTab, setSelectedTab] = useState(navMap.PROFILE);
  const navigate = useNavigate();
  const location = useLocation();
  const { isFlagEnabled } = useFeatureFlags();
  const isSelected = (path: string, tab: string) => {
    // Check if the current location includes the path OR if we're at the root and the tab is 'My Profile'
    return (
      location.pathname.includes(path) ||
      (location.pathname === '/' && tab === navMap.PROFILE)
    );
  };

  const linkStyles = (tab: string) => {
    const urlFriendlyTab = tabToUrlMap[tab];

    return {
      p: 'padding_8',
      borderRadius: '8px',
      textDecoration: 'none',
      background: isSelected(`/${urlFriendlyTab}`, tab) ? 'brand.light-green' : '',
      color: selectedTab === tab ? 'brand.army-green-dark' : 'gray.600',
      fontWeight: selectedTab === tab ? '600' : '400',
      _hover: {
        background: Colors.ArmyGreen100,
        cursor: 'pointer',
        textDecoration: 'none',
      },
    };
  };
  useEffect(() => {
    trackPageView({ sectionName: 'account-settings' });
  }, []);

  return (
    <BasePageWithTopNav>
      <PageMetadata title="Account" />
      <SidebarLayout
        sidebar={
          <Box sx={sidebarContentStyle}>
            <Text.Lead>Settings</Text.Lead>
            <Flex direction="column" sx={navListStyle}>
              <Link
                onClick={() => {
                  setSelectedTab(navMap.PROFILE);
                  navigate('/account-settings/my-profile');
                  trackSecondaryNavigation({
                    clickAction:
                      SecondaryNavigationClickActions.ACCOUNT_SETTINGS__MY_PROFILE,
                    clickLocation:
                      SecondaryNavigationClickLocations.ACCOUNT_SETTINGS__LEFT_STATIC_NAV,
                    clickText: navMap.PROFILE,
                  });
                }}
                sx={linkStyles(navMap.PROFILE)}
              >
                <FlexRow sx={menuItemStyle}>
                  <Icon name={navIconMap[navMap.PROFILE]} size={16} />
                  {navMap.PROFILE}
                </FlexRow>
              </Link>
              <Link
                onClick={() => {
                  setSelectedTab(navMap.NOTIFICATION);
                  navigate('/account-settings/notifications');
                  trackSecondaryNavigation({
                    clickAction:
                      SecondaryNavigationClickActions.ACCOUNT_SETTINGS__NOTIFICATIONS,
                    clickLocation:
                      SecondaryNavigationClickLocations.ACCOUNT_SETTINGS__LEFT_STATIC_NAV,
                    clickText: navMap.NOTIFICATION,
                  });
                }}
                sx={linkStyles(navMap.NOTIFICATION)}
              >
                <FlexRow sx={menuItemStyle}>
                  <Icon name={navIconMap[navMap.NOTIFICATION]} size={16} />
                  {navMap.NOTIFICATION}
                </FlexRow>
              </Link>
              <Link
                color={
                  selectedTab === navMap.USERS ? 'brand.army-green-dark' : 'gray.600'
                }
                onClick={() => {
                  setSelectedTab(navMap.USERS);
                  navigate('/account-settings/users');
                  trackSecondaryNavigation({
                    clickAction: SecondaryNavigationClickActions.ACCOUNT_SETTINGS__USERS,
                    clickLocation:
                      SecondaryNavigationClickLocations.ACCOUNT_SETTINGS__LEFT_STATIC_NAV,
                    clickText: navMap.USERS,
                  });
                }}
                sx={linkStyles(navMap.USERS)}
              >
                <FlexRow sx={menuItemStyle}>
                  <Icon name={navIconMap[navMap.USERS]} size={16} />
                  {navMap.USERS}
                </FlexRow>
              </Link>
              <Link
                onClick={() => {
                  setSelectedTab(navMap.BILLING);
                  navigate('/account-settings/billing');
                  trackSecondaryNavigation({
                    clickAction:
                      SecondaryNavigationClickActions.ACCOUNT_SETTINGS__BILLING,
                    clickLocation:
                      SecondaryNavigationClickLocations.ACCOUNT_SETTINGS__LEFT_STATIC_NAV,
                    clickText: navMap.BILLING,
                  });
                }}
                sx={linkStyles(navMap.BILLING)}
              >
                <FlexRow sx={menuItemStyle}>
                  <Icon name={navIconMap[navMap.BILLING]} size={16} />
                  {navMap.BILLING}
                </FlexRow>
              </Link>
              {isFlagEnabled(FLAGS.INTEGRATIONS) && (
                <Link
                  onClick={() => {
                    setSelectedTab(navMap.INTEGRATIONS);
                    navigate('/account-settings/integrations');
                    trackSecondaryNavigation({
                      clickAction:
                        SecondaryNavigationClickActions.ACCOUNT_SETTINGS__INTEGRATIONS,
                      clickLocation:
                        SecondaryNavigationClickLocations.ACCOUNT_SETTINGS__LEFT_STATIC_NAV,
                      clickText: navMap.INTEGRATIONS,
                    });
                  }}
                  sx={linkStyles(navMap.INTEGRATIONS)}
                >
                  <FlexRow sx={menuItemStyle}>
                    <Icon name={navIconMap[navMap.INTEGRATIONS]} size={16} />
                    {navMap.INTEGRATIONS}
                  </FlexRow>
                </Link>
              )}
            </Flex>
          </Box>
        }
      >
        <FlexColumn sx={containerStyle}>
          <Box sx={mainContentStyle}>
            <Outlet />
          </Box>
        </FlexColumn>
      </SidebarLayout>
    </BasePageWithTopNav>
  );
};

export default AccountSettings;
